import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-exec-amber.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/corporate-transportation-atlanta";
const PAGE_TITLE = "Atlanta Corporate Transportation | Executive Car Service Accounts";
const PAGE_DESC =
  "Dedicated corporate transportation accounts for Atlanta executives and their teams. One point of contact, simple monthly billing, background-checked chauffeurs, and live chauffeur tracking in our app, so you always know when they'll pull up.";

const faqs = [
  {
    q: "How does billing work for a corporate account?",
    a: "Every trip booked under your account is logged and consolidated into a single monthly invoice with trip date, passenger, route, and requester listed line by line. There is no card swipe at the end of a ride and no receipt to chase down, your billing contact receives one statement, approves it, and pays by ACH, check, or card on file. If your finance team needs cost codes, department tags, or a PO number on each line, we add those fields to your account at setup.",
  },
  {
    q: "Can multiple people on our team book under one account?",
    a: "Yes, and most of our corporate clients do. At setup you name a primary requester and as many backup requesters as you want authorized to book. Anyone on that list can request a trip by phone, text, or email and it bills to the same account, no separate logins, no personal cards, and no one waiting on an approval to move an executive.",
  },
  {
    q: "Does the account work for both airport transfers and hourly or point-to-point trips?",
    a: "It covers everything we run. The same account handles Hartsfield-Jackson and PDK transfers, hourly as-directed retainers for a full day of meetings, point-to-point runs across Buckhead and Midtown, evening client dinners, and multi-vehicle group moves for visiting teams. You are not managing separate arrangements for separate trip types.",
  },
  {
    q: "How are last-minute corporate bookings handled?",
    a: "Account holders get priority in scheduling, so a same-day or same-hour request is placed ahead of general inbound. Our team is staffed 24/7/365, a 6am schedule change or a meeting that runs three hours long is a phone call, not a crisis. In practice we confirm most last-minute corporate requests within a few minutes, and if the exact vehicle you usually take is committed we tell you immediately what is available instead.",
  },
  {
    q: "Is there a monthly commitment or setup cost?",
    a: "No. There is no setup fee, no monthly commitment, and no contract requiring a volume of trips. Accounts that book twice a year and accounts that book twice a week are set up the same way. You are simply paying for the rides you take, on one invoice instead of many.",
  },
  {
    q: "How does flight tracking and status updates work for executive travel?",
    a: "We take the flight number, or the tail number for private legs, at booking and track the actual aircraft rather than the scheduled time. An early arrival, a gate change, or a two-hour weather hold adjusts the chauffeur automatically. The requester gets proactive updates at each stage: chauffeur en route, chauffeur on site, passenger onboard, and passenger dropped off. Nobody on your team has to call and ask where the car is.",
  },
];

export const Route = createFileRoute("/corporate-transportation-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "corporate transportation atlanta, corporate limo service atlanta, corporate car service atlanta, executive limo service atlanta, executive assistant car service, dedicated corporate driver atlanta, monthly corporate transportation account, corporate account car service",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Corporate Transportation Atlanta",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Corporate Executive Transportation Accounts",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Sandy Springs" },
              { type: "City", name: "Alpharetta" },
              { type: "City", name: "Dunwoody" },
            ],
          })
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(faqs)),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      showCorporateForm
      eyebrow="Corporate Accounts"
      h1="Corporate Transportation Accounts for Atlanta's Executive Teams"
      intro="Your executive's schedule keeps changing. I'll help you manage the travel with one contact, monthly billing and my chauffeurs in our Mercedes-Benz Sprinter Limousines and Suburban SUVs."
      heroImage={heroImage}
      heroAlt="Executive Mercedes-Benz Sprinter staged for an Atlanta corporate transportation account"
      midImage={midImage}
      midAlt="Executive Sprinter cabin with conference seating used for Atlanta corporate travel"
      sectionOneTitle="One Account, Less Chasing"
      sectionOneBody={[
        "You shouldn't have to chase receipts or set up a new booking process for every trip. Your account brings airport transfers, meetings, dinners and visiting teams onto one monthly invoice.",
        "Tell me your requesters, billing fields and passenger preferences at setup. My team handles the details, including flight tracking and chauffeur updates.",
      ]}
      bulletsTitle="What a Corporate Account Includes"
      bulletsIntro="Set up once, and every future booking is faster and more accountable."
      bullets={[
        "One dedicated point of contact for your whole team",
        "Simple consolidated monthly billing, no expense-report chaos",
        "Background-checked, executive-protocol-trained chauffeurs",
        "Real-time flight and schedule tracking on every trip",
        "Track your chauffeur live in our app, so you always know when they'll pull up.",
        "Priority booking and 24/7 support for your account",
      ]}
      coverageTitle="Serving Atlanta's Corporate Core"
      coverageIntro="Direct coverage for the neighborhoods and business districts where our corporate clients are based."
      coverage={[
        "Buckhead",
        "Midtown Atlanta",
        "Downtown Atlanta",
        "Sandy Springs",
        "Alpharetta",
        "Perimeter / Dunwoody",
        "Hartsfield-Jackson (ATL)",
        "PDK, DeKalb-Peachtree",
      ]}
      detailSections={[
        {
          title: "Setting Up Your Corporate Account",
          body: [
            "The account is set up once. We take your company name, your billing contact, and any standing preferences, a preferred vehicle type, regular routes your executives run each week, or handling notes specific to certain principals. After that, any authorized assistant on your team can book a trip in minutes: a name, a time, a pickup, and a destination is all we need, because everything else already lives on the account.",
          ],
          list: [
            "Company name and billing contact",
            "Primary and backup requesters authorized to book",
            "Any standing preferences, vehicle type, regular routes, or VIP handling notes",
            "Preferred monthly invoicing method",
          ],
        },
        {
          title: "Built for the Person Doing the Booking",
          body: [
            "If you are requesting a ride for someone else, we treat it with exactly the same seriousness as if you were the one riding, because your name is on the arrangement. Your requests are confirmed in writing, your executive's preferences are on file so you never have to repeat them, and every trip includes proactive status updates at each stage: chauffeur en route, chauffeur on site, passenger onboard, passenger delivered. Nobody on your team has to call and check.",
          ],
        },
      ]}
      faqTitle="Corporate Account FAQs"
      faqs={faqs}
      relatedTitle="Related Corporate & Executive Services"
      related={[
        {
          to: "/private-jet-car-service-atlanta",
          title: "Private Jet Car Service Atlanta",
          desc: "For executives flying private.",
        },
        {
          to: "/chauffeur-service-atlanta",
          title: "Chauffeur Service Atlanta",
          desc: "Our full executive chauffeur offering.",
        },
        {
          to: "/atlanta-airport-car-service",
          title: "Atlanta Airport Car Service",
          desc: "Commercial airport transfers for traveling teams.",
        },
      ]}
      ctaTitle="Text Me About Your Corporate Travel"
      ctaBody="Tell me who travels, your usual routes and how your team books. I'll help you plan the account and explain billing."
    />
  );
}
